import React, { useState, useEffect } from 'react';
import { Users, CheckCircle, XCircle, Trash2, RefreshCw, ExternalLink, Instagram, Youtube, Facebook } from 'lucide-react';
import { API_URL, axiosInstance } from '../../lib/api-client';
import { shouldUseDemoMode } from '../../utils/demoMode';
import { PaginationWrapper } from '../ui/PaginationWrapper';
import { toast } from 'sonner';

interface CollaborateRequest {
    _id: string;
    name: string;
    email: string;
    contactNumber: string;
    followersCount: string;
    instagramLink?: string;
    facebookLink?: string;
    youtubeLink?: string;
    status: 'pending' | 'accepted' | 'rejected';
    adminNote?: string;
    submittedBy?: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
    };
    createdAt: string;
}

const MOCK_COLLABORATE_REQUESTS: CollaborateRequest[] = [
    {
        _id: 'mock-collab-1',
        name: 'Puneet',
        email: 'puneetkansal04@gmail.com',
        contactNumber: '9914764444',
        followersCount: '100',
        instagramLink: 'Instagram',
        facebookLink: '',
        youtubeLink: '',
        status: 'pending',
        adminNote: '',
        createdAt: new Date(Date.now() - 3600000 * 2).toISOString(),
    },
    {
        _id: 'mock-collab-2',
        name: 'Sara Khan',
        email: 'sara.k@gmail.com',
        contactNumber: '9876543210',
        followersCount: '500K',
        instagramLink: 'https://instagram.com/sara',
        facebookLink: '',
        youtubeLink: 'https://youtube.com/sara_vlogs',
        status: 'accepted',
        adminNote: 'Verified creator, high engagement rate.',
        createdAt: new Date(Date.now() - 3600000 * 24 * 3).toISOString(),
    },
    {
        _id: 'mock-collab-3',
        name: 'Rahul Verma',
        email: 'rahul.v@gmail.com',
        contactNumber: '9123456789',
        followersCount: '25K',
        instagramLink: 'https://instagram.com/rahul_fit',
        facebookLink: '',
        youtubeLink: '',
        status: 'rejected',
        adminNote: 'Follower count is below our minimum requirement.',
        createdAt: new Date(Date.now() - 3600000 * 24 * 5).toISOString(),
    }
];

export default function AdminCollaborateRequests() {
    const [requests, setRequests] = useState<CollaborateRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState({ total: 0, pages: 1 });
    const [selectedRequest, setSelectedRequest] = useState<CollaborateRequest | null>(null);
    const [actionModal, setActionModal] = useState<{ show: boolean; type: 'accept' | 'reject' | null }>({ show: false, type: null });
    const [adminNote, setAdminNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            if (shouldUseDemoMode()) {
                await new Promise(resolve => setTimeout(resolve, 500));
                
                let localRequests = localStorage.getItem('demo_collaborate_requests');
                let list: CollaborateRequest[] = [];
                if (localRequests) {
                    list = JSON.parse(localRequests);
                } else {
                    list = [...MOCK_COLLABORATE_REQUESTS];
                    localStorage.setItem('demo_collaborate_requests', JSON.stringify(list));
                }

                if (statusFilter !== 'all') {
                    list = list.filter(r => r.status === statusFilter);
                }

                const total = list.length;
                const pages = Math.ceil(total / limit);
                const startIndex = (page - 1) * limit;
                const paginatedList = list.slice(startIndex, startIndex + limit);

                setRequests(paginatedList);
                setPagination({ total, pages });
                return;
            }

            const response = await axiosInstance.get('/collaborate/admin/all', {
                params: {
                    page,
                    limit,
                    ...(statusFilter !== 'all' && { status: statusFilter }),
                }
            });

            const data = response.data;
            if (data.success) {
                setRequests(data.data.requests);
                setPagination(data.data.pagination);
            } else {
                toast.error(data.message || 'Failed to fetch requests');
            }
        } catch (err: any) {
            console.error('Failed to fetch collaborate requests:', err);
            toast.error(err.response?.data?.message || 'Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [statusFilter, page, limit]);

    const handleAction = async () => {
        if (!selectedRequest || !actionModal.type) return;
        setActionLoading(true);
        try {
            const newStatus = actionModal.type === 'accept' ? 'accepted' : 'rejected';

            if (shouldUseDemoMode()) {
                await new Promise(resolve => setTimeout(resolve, 500));

                let localRequests = localStorage.getItem('demo_collaborate_requests');
                let list: CollaborateRequest[] = localRequests ? JSON.parse(localRequests) : [...MOCK_COLLABORATE_REQUESTS];

                list = list.map(r => r._id === selectedRequest._id ? { ...r, status: newStatus, adminNote } : r);
                localStorage.setItem('demo_collaborate_requests', JSON.stringify(list));

                toast.success(`Request ${newStatus} successfully (Demo Mode)`);
                setActionModal({ show: false, type: null });
                setSelectedRequest(null);
                setAdminNote('');
                fetchRequests();
                return;
            }

            const response = await axiosInstance.patch(`/collaborate/admin/${selectedRequest._id}/status`, {
                status: newStatus,
                adminNote
            });

            const data = response.data;
            if (data.success) {
                toast.success(data.message || `Request ${newStatus} successfully.`);
                setActionModal({ show: false, type: null });
                setSelectedRequest(null);
                setAdminNote('');
                fetchRequests();
            } else {
                toast.error(data.message || 'Action failed');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'An error occurred');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this request?')) return;
        try {
            if (shouldUseDemoMode()) {
                await new Promise(resolve => setTimeout(resolve, 300));

                let localRequests = localStorage.getItem('demo_collaborate_requests');
                let list: CollaborateRequest[] = localRequests ? JSON.parse(localRequests) : [...MOCK_COLLABORATE_REQUESTS];

                list = list.filter(r => r._id !== id);
                localStorage.setItem('demo_collaborate_requests', JSON.stringify(list));

                toast.success('Request deleted successfully (Demo Mode)');
                fetchRequests();
                return;
            }

            const response = await axiosInstance.delete(`/collaborate/admin/${id}`);
            const data = response.data;
            if (data.success) {
                toast.success(data.message || 'Request deleted successfully');
                fetchRequests();
            } else {
                toast.error(data.message || 'Delete failed');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'An error occurred');
        }
    };

    const statusBadge = (status: string) => {
        const map: Record<string, string> = {
            pending: 'bg-yellow-100 text-yellow-800',
            accepted: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || 'bg-gray-100 text-gray-700'}`}>
                {status}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Collaborate Requests</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage collaboration requests from influencers & content creators</p>
                </div>
                <button
                    onClick={fetchRequests}
                    className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {(['all', 'pending', 'accepted', 'rejected'] as const).map((s) => (
                    <button
                        key={s}
                        onClick={() => { setStatusFilter(s); setPage(1); }}
                        className={`p-4 rounded-xl border text-left transition-all ${statusFilter === s
                            ? 'border-pink-500 bg-pink-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                    >
                        <p className={`text-xs font-semibold uppercase tracking-wide ${statusFilter === s ? 'text-pink-600' : 'text-gray-500'}`}>
                            {s === 'all' ? 'All Requests' : s}
                        </p>
                        <p className={`text-xl font-bold mt-1 ${statusFilter === s ? 'text-pink-700' : 'text-gray-800'}`}>
                            {statusFilter === s ? pagination.total : '—'}
                        </p>
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border overflow-hidden shadow-sm">
                {loading ? (
                    <div className="p-12 text-center text-gray-500">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-pink-400" />
                        <p>Loading requests...</p>
                    </div>
                ) : requests.length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                        <Users className="w-12 h-12 mx-auto mb-3" />
                        <p className="font-medium">No requests found</p>
                        <p className="text-sm mt-1">No collaborate requests for the selected filter</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px]">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        {['Name', 'Contact', 'Followers', 'Social Links', 'Status', 'Date', 'Actions'].map((h) => (
                                            <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {requests.map((req) => (
                                        <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-gray-900">{req.name}</div>
                                                <div className="text-xs text-gray-500">{req.email}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-sm text-gray-700">{req.contactNumber}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-semibold">
                                                    <Users className="w-3 h-3" />
                                                    {req.followersCount}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex gap-2">
                                                    {req.instagramLink && (
                                                        <a href={req.instagramLink} target="_blank" rel="noreferrer"
                                                            className="text-pink-500 hover:text-pink-700" title="Instagram">
                                                            <Instagram className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {req.facebookLink && (
                                                        <a href={req.facebookLink} target="_blank" rel="noreferrer"
                                                            className="text-blue-500 hover:text-blue-700" title="Facebook">
                                                            <Facebook className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {req.youtubeLink && (
                                                        <a href={req.youtubeLink} target="_blank" rel="noreferrer"
                                                            className="text-red-500 hover:text-red-700" title="YouTube">
                                                            <Youtube className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {!req.instagramLink && !req.facebookLink && !req.youtubeLink && (
                                                        <span className="text-gray-400 text-xs">—</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">{statusBadge(req.status)}</td>
                                            <td className="px-4 py-3 text-xs text-gray-500">
                                                {new Date(req.createdAt).toLocaleDateString('en-IN', {
                                                    day: '2-digit', month: 'short', year: 'numeric'
                                                })}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-1">
                                                    {req.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => { setSelectedRequest(req); setActionModal({ show: true, type: 'accept' }); }}
                                                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg" title="Accept"
                                                            >
                                                                <CheckCircle className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => { setSelectedRequest(req); setActionModal({ show: true, type: 'reject' }); }}
                                                                className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Reject"
                                                            >
                                                                <XCircle className="w-4 h-4" />
                                                            </button>
                                                        </>
                                                    )}
                                                    {req.status !== 'pending' && (
                                                        <button
                                                            onClick={() => { setSelectedRequest(req); setActionModal({ show: true, type: req.status === 'accepted' ? 'reject' : 'accept' }); }}
                                                            className="px-2 py-1 text-xs border rounded-lg hover:bg-gray-50 text-gray-600"
                                                        >
                                                            {req.status === 'accepted' ? 'Reject' : 'Accept'}
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDelete(req._id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="px-6 py-4 border-t">
                            <PaginationWrapper
                                currentPage={page}
                                totalPages={pagination.pages}
                                totalItems={pagination.total}
                                itemsPerPage={limit}
                                onPageChange={(p) => setPage(p)}
                                onItemsPerPageChange={(l) => {
                                    setLimit(l);
                                    setPage(1);
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Action Modal */}
            {actionModal.show && selectedRequest && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-1">
                                {actionModal.type === 'accept' ? '✅ Accept Request' : '❌ Reject Request'}
                            </h2>
                            <p className="text-gray-500 text-sm mb-4">
                                For: <strong>{selectedRequest.name}</strong> ({selectedRequest.email})
                            </p>

                            {/* Profile Summary */}
                            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-1.5 text-sm">
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Contact:</span>
                                    <span className="font-medium">{selectedRequest.contactNumber}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Followers:</span>
                                    <span className="font-medium">{selectedRequest.followersCount}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Links:</span>
                                    <div className="flex gap-2">
                                        {selectedRequest.instagramLink && <a href={selectedRequest.instagramLink} target="_blank" rel="noreferrer" className="text-pink-500 hover:underline flex items-center gap-1"><Instagram className="w-3 h-3" />IG</a>}
                                        {selectedRequest.facebookLink && <a href={selectedRequest.facebookLink} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline flex items-center gap-1"><Facebook className="w-3 h-3" />FB</a>}
                                        {selectedRequest.youtubeLink && <a href={selectedRequest.youtubeLink} target="_blank" rel="noreferrer" className="text-red-500 hover:underline flex items-center gap-1"><Youtube className="w-3 h-3" />YT</a>}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-5">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Admin Note <span className="text-gray-400">(optional)</span>
                                </label>
                                <textarea
                                    value={adminNote}
                                    onChange={(e) => setAdminNote(e.target.value)}
                                    rows={3}
                                    placeholder="Add a note for internal records..."
                                    className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-300"
                                />
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => { setActionModal({ show: false, type: null }); setAdminNote(''); setSelectedRequest(null); }}
                                    className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50"
                                >Cancel</button>
                                <button
                                    onClick={handleAction}
                                    disabled={actionLoading}
                                    className={`px-5 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-60 ${actionModal.type === 'accept'
                                        ? 'bg-green-600 hover:bg-green-700'
                                        : 'bg-red-500 hover:bg-red-600'
                                        }`}
                                >
                                    {actionLoading ? 'Processing...' : actionModal.type === 'accept' ? 'Accept' : 'Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
